"use client";

import { createContext, useState, useEffect, useContext } from "react";

import envConfig from "@/config/envConfig";
import { UserType } from "@/types/user";
import { toUserType } from "@/utils/userUtils";
import { useMessage } from "./MessageProvider";
import { userLogout } from "@/services/userService";
import { getLocalStorageItem } from "@/utils/getLocalStorageItem";
import { getAuthToken, setAuthToken } from "@/utils/authToken";
import { getLabel } from "@/data/labels";

const AUTH_STORAGE_KEY = "user";

function getStoredUser(): UserType | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = getLocalStorageItem(AUTH_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as UserType;
  } catch {
    // ignore invalid stored data
  }
  return null;
}

interface AuthContextType {
  user: UserType | null;
  setUser: React.Dispatch<React.SetStateAction<UserType | null>>;
  refreshAuthentication: () => Promise<void>;
  logout: () => Promise<void>;
  isUserLoading: boolean;
}

// Create a context for authentication data
export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(true);

  const { showMessage } = useMessage();

  // On client mount (and after locale change when provider remounts): restore from localStorage
  // then validate with server. Do not clear user when server returns no session if we still have
  // a stored user (keeps user logged in when switching language).
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.document === "undefined") return;

    // Grab token from URL if redirected from OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get("token");
    if (tokenFromUrl) {
      setAuthToken(tokenFromUrl);
      try {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } catch (e) {}
    }

    const stored = getStoredUser();
    if (stored) setUser(stored);
    refreshAuthentication();
  }, []);

  // Function to log out the user — always clear local state first so logout works even if the API fails (e.g. cross-origin cookie)
  async function logout() {
    setUser(null);
    setAuthToken(null);
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // ignore
    }
    const currentLang = (typeof window !== "undefined" && window.location?.pathname?.match(/^\/([\w]{2})-/)?.[1]) || "en";
    showMessage("success", getLabel(currentLang as AppLocale, "LOGOUT_SUCCESS"));

    try {
      await userLogout();
    } catch (error) {
      console.error("Logout API failed (session already cleared):", error);
    }
  }

  // Function to refresh the authentication state (validates with server, keeps user on error)
  async function refreshAuthentication() {
    try {
      setIsUserLoading(true);

      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      const token = getAuthToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(`${envConfig.publicServerUrl}/api/users/me?t=${Date.now()}`, {
        method: "GET",
        headers,
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json().catch(() => ({}));
      const nextUser = toUserType(data?.user ?? null);

      if (nextUser) {
        setUser(nextUser);
        try {
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextUser));
          if (data?.token) setAuthToken(data.token);
        } catch {
          // ignore
        }
      } else if (response.status === 401 || response.status === 403) {
        setUser(null);
        setAuthToken(null);

        try {
          localStorage.removeItem(AUTH_STORAGE_KEY);
        } catch {
          // ignore
        }
      }
      setIsUserLoading(false);
    } catch (error) {
      console.error("Failed to refresh authentication:", error);
      setIsUserLoading(false);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        refreshAuthentication,
        logout,
        isUserLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }
  return context;
};
